import React from 'react';
import get from 'lodash/get';
import { noop } from 'utils/noop';
import { SemanticUI } from '@iterable/deprecated_react_components';
import ValueBox from './ValueBox';
import {
  AddButton,
  NumberInputContainer,
  StyledRangeSlider,
  StyledRangeSliderHandle,
} from './RangeSlider.styled';

// type Props = {
//   /** Minimum value of range. */
//   min: number,
//   /** Maximum value of range. */
//   max: number,
//   /** The maximum number of ranges allowed. */
//   maxRanges: number,
//   /**
//    * Callback function when either value or color changes of a range.
//    *
//    * ```
//    * onChange(e, newValues)
//    * ```
//    * @param { Object } e: The change event.
//    * @param { Array } newValues: The new values of the range.
//    * @returns { void }
//    */
//   onChange: (e: {}, newValues: [number]) => void,
//   /**
//    * Callback function when minimum value changes.
//    *
//    * ```
//    * onChangeMin(e, min)
//    * ```
//    * @param { Object } e: The change event.
//    * @param { Number } min: The new min value.
//    * @returns { void }
//    */
//   onChangeMin: (e: {}, min: number) => void,
//   /**
//    * Callback function when maximum value changes.
//    *
//    * ```
//    * onChangeMax(e, max)
//    * ```
//    * @param { Object } e: The change event.
//    * @param { Number } max: The new max value.
//    * @returns { void }
//    */
//   onChangeMax: (e: {}, max: number) => void,
//   /** Sorted array of object values containining number value of range and string hex color. */
//   value: [{ color: string, value: number }],
// };

export default class RangeSlider extends React.Component {
  static defaultProps = {
    min: 0,
    max: 100,
    maxRanges: 10,
    onChange: noop,
    onChangeMin: noop,
    onChangeMax: noop,
    value: [],
  };

  /**
   * Checks the input value if it contains the min and max values and adds them if missing.
   * @returns { void }
   */
  componentDidMount = () => {
    const { onChange, min, max, value } = this.props;
    const values = value.map((val) => val.value);
    const newValues = value;
    const minValue = parseFloat(min, 10);
    const maxValue = parseFloat(max, 10);
    const missingMin = values.indexOf(minValue) === -1;
    const missingMax = values.indexOf(maxValue) === -1;

    if (missingMin) {
      newValues.unshift({
        min: true,
        value: minValue,
        color: '#eb144c',
      });
    }
    if (missingMax) {
      newValues.push({
        max: true,
        value: maxValue,
        color: '#00d034',
      });
    }
    if (missingMin || missingMax) {
      onChange({}, newValues);
    }
  };

  /**
   * generateHandle
   * Generates the slider handles
   * @param { Object } props: Props of the handle passed from Slider.
   * @property { boolean } props.dragging: whether dragging is active.
   * @property { string } props.index: the index of the selected item.
   * @returns { React.ReactNode } The handle element.
   */
  generateHandle = (props) => {
    const { value: values } = this.props;
    const { value, dragging, index, ...restProps } = props;
    return (
      <StyledRangeSliderHandle
        $isActive={dragging}
        key={index}
        value={value}
        data-min={index === 0 || undefined}
        data-max={index === values.length - 1 || undefined}
        {...restProps}
        disabled
      >
        <div className="cssHandleWrapper" data-test="core-rangeSlider-handle">
          <div className="cssPopup">{value}</div>
        </div>
      </StyledRangeSliderHandle>
    );
  };

  /**
   * handleChangeHandle
   * Callback when a handle position and value changes.
   * @param { Number } newValue: The updated values of all the handles.
   * @returns { void }
   */
  handleChangeHandle = (newValue) => {
    let minValueChanged = false;
    let maxValueChanged = false;
    const { onChange, value } = this.props;
    const newValues = newValue.map((val, index) => {
      const isMin = index === 0;
      const isMax = index === value.length - 1;

      if (isMin && val !== value[0].value) {
        minValueChanged = true;
      }
      if (isMax && val !== value[value.length - 1].value) {
        maxValueChanged = true;
      }

      return {
        value: val,
        color: get(value[index], 'color'),
      };
    });

    if (!minValueChanged && !maxValueChanged) {
      onChange({}, newValues);
    }
  };

  /**
   * handleChangeColor
   * Callback function when color value changes.
   * @param { Object } e: The change event.
   * @param { Object } colorProps: The props of the color picker including the new color value.
   * @returns { void }
   */
  handleChangeColor = (e, { index, color }) => {
    const { onChange, value: values } = this.props;
    const newValues = values;
    newValues[index].color = color;
    onChange(e, newValues);
  };

  handleChangeMin = (e, min) => {
    this.props.onChangeMin(e, min);
  };

  handleChangeMax = (e, max) => {
    this.props.onChangeMax(e, max);
  };

  /**
   * handleChangeValue
   * Callback when the number value changes from the number input in the value box.
   * @param { Object } e: the change event.
   * @param { Obejct } valueBoxProps: Props of the value box including the changed value.
   * @returns { void }
   */
  handleChangeValue = (e, { index, value }) => {
    const { onChange, value: values } = this.props;
    const parsedValue = parseFloat(value, 10) || 0;
    const newValues = values.map(({ color, value: val }, i) => ({
      color,
      value: index === i ? parsedValue : val,
    }));
    onChange(e, newValues);
  };

  /**
   * handleClickAddRange
   * Callback function when add range button is clicked.
   * Adds a new range in between selected ranges.
   * @param { Object } e: The click event.
   * @param { Objecy } addButtonProps: Props of the add button including the button index.
   * @returns { void }
   */
  handleClickAddRange = (index) => {
    const { onChange, value } = this.props;
    const newValues = value;
    const firstValue = value[index].value;
    const secondValue = value[index + 1].value;
    const newValueDiff = secondValue - firstValue;
    const newValueOffset = Math.floor(newValueDiff / 2);

    newValues.splice(index + 1, 0, {
      color: '#0693e3',
      value: firstValue + newValueOffset,
    });
    onChange({}, newValues);
  };

  /**
   * handleDelete
   * Callback function when a range is deleted.
   * @param { Object } e: The delete event.
   * @param { Objecy } deleteButtonProps: Props of the delete button including the button index.
   * @returns { void }
   */
  handleDelete = (e, { index }) => {
    const { onChange, value } = this.props;
    const newValues = value.filter((val, i) => i !== index);
    onChange(e, newValues);
  };

  /**
   * generateValueBoxes
   * Creates the corresponding value boxes of the range slider handles.
   * @returns { Array } generated value boxes.
   */
  generateValueBoxes = () => {
    const { min, max, maxRanges, value: values } = this.props;
    return values.map(({ color, value }, index) => {
      const isMin = index === 0;
      const isMax = index === values.length - 1;
      const lowerLimit = isMin ? min : values[index - 1].value;
      const upperLimit = isMax ? max : values[index + 1].value;

      return (
        <div key={`value-${index}`}>
          <ValueBox
            color={color}
            index={index}
            isMin={isMin}
            isMax={isMax}
            onChangeColor={this.handleChangeColor}
            onChangeMin={this.handleChangeMin}
            onChangeMax={this.handleChangeMax}
            onChangeValue={this.handleChangeValue}
            onDelete={this.handleDelete}
            disableIncrement={parseFloat(value, 10) + 1 === parseFloat(upperLimit, 10)}
            disableDecrement={parseFloat(value, 10) - 1 === parseFloat(lowerLimit, 10)}
            minValue={parseFloat(lowerLimit, 10)}
            maxValue={parseFloat(upperLimit, 10)}
            value={value || 0}
          />
          {!isMax && (
            <AddButton
              onClick={() => this.handleClickAddRange(index)}
              index={index}
              disabled={values.length >= maxRanges}
            >
              <div className="cssAddLabel">
                <SemanticUI.Icon name="plus" />
              </div>
              <div className="cssAddLine" />
            </AddButton>
          )}
        </div>
      );
    });
  };

  render() {
    const { min, max, value } = this.props;
    const values = value.map((val) => val.value || 0);
    const styles = value.map((val) => ({ background: val.color }));

    return (
      <div>
        <StyledRangeSlider
          handle={this.generateHandle}
          min={min}
          max={max}
          onChange={this.handleChangeHandle}
          handleStyle={styles}
          trackStyle={styles}
          value={values}
        />
        <NumberInputContainer>{this.generateValueBoxes()}</NumberInputContainer>
      </div>
    );
  }
}
